/**
 * KPI Form Store - KPI/OKR 추가/수정 폼 관리 (리팩토링 버전)
 *
 * @description 새 목표 추가, KPI 수정 관련 상태 및 기능
 *              참고 KPI: useRefKpiStore
 *
 * @usage
 * const { openAddGoalModal, saveNewGoal, updateKpi } = useKpiFormStore();
 */
import { ref, nextTick, computed } from 'vue';
import { useKpiApi } from '~/composables/performance/kpi/useKpiApi';
import { useKrCascadingTree } from '~/composables/performance/kpi/useKrCascadingTree';
import { useUiToast } from '~/composables/useUiToast';
import { showConfirmDialog } from '~/composables/useConfirmDialog';
import { kpiT } from '~/composables/performance/kpi/useKpiI18n';
import { useMenuStore } from '~/stores/useMenuStore';

/** 셀렉트 빈 값 옵션 (상위KPI, 부서KPI, 이전연도 등) */
const emptySelectOption = (): SelectOption => ({ value: '', label: kpiT('form.notSelected') });

/**
 * 업무 메뉴 권한 여부
 * - 구독과 별개로 메뉴 미사용 처리가 가능하므로 메뉴 path 권한으로 판단
 * - selectData.do 등 업무 모듈 API 호출 가능 여부 체크에 사용
 */
const hasAvtMenu = computed(() => {
  const menuStore = useMenuStore();
  return (
    menuStore.menuSections.length > 0 &&
    (menuStore.hasPathPermission('/task/home') || menuStore.hasPathPermission('/screens/task/home'))
  );
});

// 모달 폼 내부 포커스 헬퍼 (data-kpi-field 기준 — i18n placeholder와 무관)
const focusFormField = fieldKey => {
  nextTick(() => {
    const el = document.querySelector(`.goal-form [data-kpi-field="${fieldKey}"]`);
    el?.focus();
  });
};

// OKR 모달 내부 포커스 헬퍼
const focusOkrField = selector => {
  nextTick(() => {
    const el = document.querySelector(`.kpi-goal-modal ${selector}`);
    el?.focus();
  });
};

// 공유 상태 import
import {
  selectedYear,
  selectedMember,
  loginUserInfo,
  kpiList,
  // 합의/기간 제어 플래그(AS-IS 이관)
  agreementYn,
  agreementEditYn,
  kpiConfigTermYn,
  memberScoreVersion,
} from './useKpiSharedState';

// 공통 유틸리티 import
import {
  mapKpiListItem,
  createDefaultKpiForm,
  createDefaultOkrForm,
  formatDateForApi,
  buildKpiSaveData,
} from './useKpiUtils';

// 분리된 store import (re-export용)
import { useRefKpiStore, useRefKpiState } from './useRefKpiStore';
import { useOkrState, useOkrStore } from './useOkrStore';

import type {
  SelectOption,
  KpiItem,
  KpiFormData,
  OkrFormData,
  GoalTabType,
  OkrOptionItem,
  PlanOptionItem,
  PrevKpiItem,
  ObjectiveOptionItem,
  AddedKrItem,
  KpiLinkItem,
  KpiLinkData,
  UpKpiResponseItem,
  DeptKpiResponseItem,
  PlanResponseItem,
  OkrObjResponseItem,
  KpiListResponseItem,
  RefUserItem,
  RefKpiItem,
} from './useKpiTypes';

// ============================================
// 싱글톤 상태 변수
// ============================================

// kpi 연계도 모달
const showKpiLinkModal = ref(false); // 모달 열림 상태
const kpiLinkData = ref<KpiLinkData>({
  // 연계도 데이터
  parentKpi: null, // 상위 KPI
  currentKpi: null, // 현재 KPI
  childKpiList: [], // 하위 KPI 목록
});

// kr 연계도 모달
const showKrLinkModal = ref(false);
const krLinkData = ref({
  currentKr: null, // 현재 KR
  tree: [], // KR 연계도 트리 (AS-IS SvgTree 대체, useKrCascadingTree 파싱 결과)
});

// 새 목표 추가 모달
const showAddGoalModal = ref(false);
const activeGoalTab = ref<GoalTabType>('KPI');
const goalTabs = ref([
  { id: 'KPI', label: 'KPI' },
  { id: 'OKR', label: 'OKR' },
]);

// KPI 폼 데이터
const kpiForm = ref<KpiFormData>(createDefaultKpiForm());

// OKR 폼 데이터
const okrForm = ref<OkrFormData>(createDefaultOkrForm());

// OKR 수정 폼 데이터
const editingOkrData = ref<OkrFormData>(createDefaultOkrForm());

// OKR 수정 모달 상태
const showEditOkrModal = ref(false);
const editingOkrId = ref('');

// KPI 수정 모달 상태
const showEditKpiModal = ref(false);
const editingKpiId = ref<string>('');
const editingKpiData = ref<KpiItem | null>(null);

// 옵션 목록 (모듈 로드 시 i18n 미준비 → emptySelectOption() 호출하지 않음)
const parentKpiOptions = ref<SelectOption[]>([{ value: '', label: '' }]);
const departmentKpiOptions = ref<SelectOption[]>([]);
const kpiSelectOptions = ref<SelectOption[]>([]);
const okrOptions = ref<OkrOptionItem[]>([]);
const planOptions = ref<PlanOptionItem[]>([]);

// OKR 선택 모달용 원본 데이터 (트리 구조 표시용)
const okrListRaw = ref<PlanResponseItem[]>([]);
const okrCategoryList = ref<PlanResponseItem[]>([]);
const okrBoardList = ref<PlanResponseItem[]>([]);

// 업무 옵션 로드 목록
const planBoardList = ref([]);
const planCategoryList = ref([]);

// OKR 옵션 로드 race condition 방지용 버전
let loadOkrOptionsVersion = 0;
const prevYearOptions = ref<SelectOption[]>([]);
const prevKpiOptions = ref<PrevKpiItem[]>([]);
const objectiveOptions = ref<ObjectiveOptionItem[]>([]);

// 배포 대상 관련
const showRefUserModal = ref(false);
const selectedRefUsers = ref<RefUserItem[]>([]);
const refUserSearchKeyword = ref('');

// OKR 배포 대상
const showOkrRefUserModal = ref(false);
const selectedOkrRefUsers = ref<RefUserItem[]>([]);

// KR 추가 모달
const addedKrList = ref<AddedKrItem[]>([]);
const showKrAddModal = ref(false);
const newKrForm = ref({ krNm: '', weight: '' });

// ============================================
// State Export
// ============================================
export const useKpiFormState = () => {
  // 분리된 store의 상태 가져오기
  const refKpiState = useRefKpiState();

  return {
    // KPI 연계도
    showKpiLinkModal,
    kpiLinkData,

    // KR 연계도
    showKrLinkModal,
    krLinkData,

    // 새 목표 모달
    showAddGoalModal,
    activeGoalTab,
    goalTabs,
    kpiForm,
    okrForm,

    // 수정 모달
    showEditKpiModal,
    editingKpiId,
    editingKpiData,
    showEditOkrModal,
    editingOkrId,
    editingOkrData,

    // 옵션
    parentKpiOptions,
    departmentKpiOptions,
    kpiSelectOptions,
    okrOptions,
    planOptions,
    planBoardList,
    planCategoryList,
    prevYearOptions,
    prevKpiOptions,
    objectiveOptions,

    // 업무 메뉴 권한
    hasAvtMenu,

    // OKR 선택 모달용 원본 데이터
    okrListRaw,
    okrCategoryList,
    okrBoardList,

    // 배포 대상
    showRefUserModal,
    selectedRefUsers,
    refUserSearchKeyword,
    showOkrRefUserModal,
    selectedOkrRefUsers,

    // KR 추가
    addedKrList,
    showKrAddModal,
    newKrForm,

    // 참고 KPI (분리된 store에서 가져옴)
    ...refKpiState,
  };
};

// ============================================
// Method Export
// ============================================
export const useKpiFormStore = () => {
  const kpiApi = useKpiApi();
  const { syncAllOkrCaches } = useOkrStore();

  // 분리된 store
  const refKpiStore = useRefKpiStore();

  // ============================================
  // KPI 연계도
  // ============================================
  // KPI 연계도 모달 열기
  const openKpiLinkModal = async (kpi: KpiItem) => {
    // 먼저 모달 열고 현재 KPI 표시
    kpiLinkData.value = {
      parentKpi: null,
      currentKpi: kpi,
      childKpiList: [],
    };
    showKpiLinkModal.value = true;

    // API 호출해서 상위/하위 KPI 조회
    try {
      const response = await kpiApi.fetchKpiLinkData(selectedYear.value, kpi.kpiId);
      if (response) {
        // API 응답의 currentKpi가 빈 문자열이면 프론트 kpi 데이터 사용
        const currentKpi = (response.currentKpi && typeof response.currentKpi === 'object')
          ? { ...kpi, ...response.currentKpi }
          : kpi;

        kpiLinkData.value = {
          parentKpi: (response.parentKpi && typeof response.parentKpi === 'object') ? response.parentKpi : null,
          currentKpi,
          childKpiList: response.childKpiList || [],
        };
      }
    } catch (error) {
      console.error('KPI 연계도 조회 실패:', error);
    }
  };

  // KPI 연계도 모달 닫기
  const closeKpiLinkModal = () => {
    showKpiLinkModal.value = false;
  };

  // ============================================
  // KR 연계도
  // ============================================
  // KR 연계도 모달 열기
  const openKrLinkModal = async (kr) => {
    // 모달 열고 현재 KR 표시
    krLinkData.value = {
      currentKr: kr,
      tree: [],
    };
    showKrLinkModal.value = true;

    try {
      const krId = kr.planId || kr.krId;

      // 분기(cycleId)/소유자 확보 — kr 행에 없으면 KR 상세에서 보강
      let cycleId = kr.cycleId || '';
      let ownerUserId = kr.userId || selectedMember.value || '';
      if (!cycleId) {
        try {
          const detailRes = await kpiApi.fetchKrDetail(selectedYear.value, krId, ownerUserId);
          const d = detailRes?.detailData || {};
          cycleId = d.cycleId || '';
          ownerUserId = ownerUserId || d.userId || '';
        } catch {
          /* cycleId 미확보 시 빈 값으로 진행 (백엔드 분기 필터 무시) */
        }
      }

      // AS-IS SvgTree와 동일한 다단계 cascade XML 조회 → 트리 파싱
      // (백엔드가 nodeRemoveCnt로 중첩 완료한 XML을 그대로 트리화)
      const { parseCascadingXml } = useKrCascadingTree();
      const xml = await kpiApi.fetchKrCascadingTree(
        selectedYear.value,
        cycleId,
        krId,
        ownerUserId,
      );
      krLinkData.value.tree = parseCascadingXml(xml);
    } catch (error) {
      console.error('KR 연계도 조회 실패:', error);
      krLinkData.value.tree = [];
    }
  };

  // KR 연계도 모달 닫기
  const closeKrLinkModal = () => {
    showKrLinkModal.value = false;
  };

  // ============================================
  // 폼 초기화
  // ============================================

  /**
   * KPI 폼 초기화
   */
  const resetKpiForm = () => {
    kpiForm.value = createDefaultKpiForm();
    selectedRefUsers.value = [];
  };

  /**
   * OKR 폼 초기화
   */
  const resetOkrForm = () => {
    okrForm.value = createDefaultOkrForm();
    selectedOkrRefUsers.value = [];
    addedKrList.value = [];
  };

  /**
   * OKR 수정 폼 초기화
   */
  const resetEditingOkrData = () => {
    editingOkrData.value = createDefaultOkrForm();
  };

  // ============================================
  // 옵션 로드
  // ============================================

  /**
   * 상위 KPI 옵션 로드
   *
   * AS-IS와 동일하게 findYear만 전송한다. 백엔드(`KpiMngServiceImpl.selectUpKpiList`)가
   * 세션 사용자(`SessionUtil.getUserId()`)로 userId를 강제 세팅하므로, 프론트에서 selectedMember
   * 같은 다른 사용자 ID를 보내면 빈 결과가 돌아오는 경우가 있다.
   */
  const loadParentKpiOptions = async () => {
    try {
      const response = await kpiApi.fetchUpKpiList(selectedYear.value);

      const kpiListData = response?.list || response?.upKpiList || response?.kpiList || [];

      if (Array.isArray(kpiListData) && kpiListData.length > 0) {
        parentKpiOptions.value = [
          emptySelectOption(),
          ...kpiListData.map((item: UpKpiResponseItem) => {
            const kpiId = item.managerKpiId || item.kpiId || item.id || '';
            const kpiNm = item.managerKpiNm || item.kpiNm || item.text || '';
            const managerPrefix =
              item.managerNm && item.deptNm
                ? `[${item.managerNm}-${item.deptNm}] `
                : item.managerNm
                  ? `[${item.managerNm}] `
                  : '';
            return {
              value: kpiId,
              label: `${managerPrefix}${kpiNm}`,
            };
          }),
        ];
      } else {
        parentKpiOptions.value = [emptySelectOption()];
      }
    } catch (error) {
      console.error('상위 KPI 옵션 로드 실패:', error);
      parentKpiOptions.value = [emptySelectOption()];
    }
  };

  /**
   * 부서 KPI 옵션 로드
   */
  const loadDeptKpiOptions = async () => {
    try {
      const response = await kpiApi.fetchDeptKpiList(selectedYear.value);
      const kpiListData = response?.deptKpiList || response?.list || [];

      if (Array.isArray(kpiListData) && kpiListData.length > 0) {
        departmentKpiOptions.value = [
          emptySelectOption(),
          ...kpiListData.map((item: DeptKpiResponseItem) => ({
            value: item.kpiId || item.id || '',
            label: item.kpiNm || item.text || '',
          })),
        ];
      } else {
        departmentKpiOptions.value = [emptySelectOption()];
      }
    } catch (error) {
      console.error('부서 KPI 옵션 로드 실패:', error);
      departmentKpiOptions.value = [emptySelectOption()];
    }
  };

  /**
   * 연결 가능한 KPI 목록 로드
   */
  const loadKpiSelectOptions = async () => {
    try {
      kpiSelectOptions.value = kpiList.value.map(kpi => ({
        value: kpi.kpiId,
        label: kpi.kpiNm,
      }));
    } catch (error) {
      console.error('KPI 옵션 로드 실패:', error);
      kpiSelectOptions.value = [];
    }
  };

  /**
   * OKR 옵션 로드
   */
  const loadOkrOptions = async (cycleId?: string) => {
    const currentVersion = ++loadOkrOptionsVersion;

    try {
      const okrState = useOkrState();
      const targetCycleId = cycleId !== undefined ? cycleId : okrState.selectedCycleId.value;
      const targetUserId = selectedMember.value || loginUserInfo.value?.userId || '';

      const response = await kpiApi.fetchLinkableOkrList(
        selectedYear.value,
        targetUserId,
        targetCycleId,
      );

      if (currentVersion !== loadOkrOptionsVersion) return;

      if (response?.planList && Array.isArray(response.planList)) {
        const filteredList = targetCycleId
          ? response.planList.filter((item: PlanResponseItem) => item.boardId === targetCycleId)
          : response.planList;

        okrOptions.value = filteredList.map((item: PlanResponseItem) => ({
          krId: item.planId || '',
          krNm: item.planNm || '',
          objectiveNm: item.categoryNm || '',
        }));

        okrListRaw.value = response.planList || [];
        okrCategoryList.value = response.categoryList || [];
        okrBoardList.value = response.boardList || [];
      } else {
        okrOptions.value = [];
        okrListRaw.value = [];
        okrCategoryList.value = [];
        okrBoardList.value = [];
      }
    } catch (error) {
      if (currentVersion !== loadOkrOptionsVersion) return;
      console.error('OKR 옵션 로드 실패:', error);
      okrOptions.value = [];
      okrListRaw.value = [];
      okrCategoryList.value = [];
      okrBoardList.value = [];
    }
  };

  /**
   * 업무(Plan) 옵션 로드
   */
  const loadPlanOptions = async (options?: { skipBoardCategory?: boolean }) => {
    try {
      // 업무 연결용 plan 목록 + 보드 목록 조회
      const response = await kpiApi.fetchLinkablePlanList(
        selectedYear.value,
        selectedMember.value || loginUserInfo.value?.userId || '',
      );

      if (response?.planList && Array.isArray(response.planList)) {
        planOptions.value = response.planList.map((item: PlanResponseItem) => ({
          planId: item.planId || '',
          planNm: item.planNm || '',
          boardNm: item.boardNm || '',
          categoryNm: item.categoryNm || '',
        }));

        // 보드 목록 저장
        planBoardList.value = response.boardList || [];
      }

      // KR 팝업은 업무 모듈 selectData.do 를 호출하지 않는다.
      // 카테고리는 selectCategoryPlan_json.do 의 categoryList 를 사용한다.
      // 업무 메뉴 미사용/미권한 시 selectData.do 호출하면 권한 오류가 나므로 스킵.
      if (options?.skipBoardCategory || !hasAvtMenu.value) {
        planCategoryList.value = response?.categoryList || [];
        return;
      }

      // 새 업무 등록용 전체 보드-카테고리 목록 조회
      // selectCategoryPlan_json.do의 categoryList는 plan이 있는 카테고리만 반환하므로,
      // selectData.do의 boardCategoryList를 사용하여 전체 카테고리 조회
      const boardCategoryRes = await kpiApi.fetchAllBoardCategoryList();
      planCategoryList.value = boardCategoryRes?.boardCategoryList || [];
    } catch (error) {
      console.error('업무 옵션 로드 실패:', error);
      planOptions.value = [];
      planBoardList.value = [];
      planCategoryList.value = [];
    }
  };

  /**
   * Objective 옵션 로드
   */
  const loadObjectiveOptions = async (cycleId?: string) => {
    try {
      // fetchOkrListByCycle로 목적 목록 조회
      const response = await kpiApi.fetchOkrListByCycle(
        selectedYear.value,
        cycleId || '',
        selectedMember.value || loginUserInfo.value?.userId || '',
      );

      if (response?.objList && Array.isArray(response.objList)) {
        objectiveOptions.value = response.objList.map((item: OkrObjResponseItem) => ({
          objId: item.objId || '',
          objNm: item.objNm || '',
        }));
      }
    } catch (error) {
      console.error('Objective 옵션 로드 실패:', error);
      objectiveOptions.value = [];
    }
  };

  /**
   * 이전연도 옵션 로드
   */
  const loadPrevYearOptions = () => {
    const currentYear = parseInt(selectedYear.value);
    prevYearOptions.value = [
      emptySelectOption(),
      { value: String(currentYear - 1), label: `${currentYear - 1}년` },
      { value: String(currentYear - 2), label: `${currentYear - 2}년` },
    ];
  };

  /**
   * 이전연도 KPI 옵션 로드
   */
  const loadPrevKpiOptions = async (year: string) => {
    if (!year) {
      prevKpiOptions.value = [];
      return;
    }

    try {
      const response = await kpiApi.fetchKpiList(
        year,
        selectedMember.value || loginUserInfo.value?.userId || '',
      );

      if (response?.kpiList && Array.isArray(response.kpiList)) {
        prevKpiOptions.value = response.kpiList.map((item: KpiListResponseItem) => ({
          kpiId: item.kpiId || '',
          kpiNm: item.kpiNm || '',
          year: year,
        }));
      }
    } catch (error) {
      console.error('이전연도 KPI 로드 실패:', error);
      prevKpiOptions.value = [];
    }
  };

  /**
   * 성과 연동 옵션 일괄 로드
   */
  const loadPerformanceLinkOptions = async () => {
    await Promise.all([loadOkrOptions(''), loadPlanOptions(), loadObjectiveOptions()]);
    loadPrevYearOptions();
  };

  // ============================================
  // 새 목표 추가 모달
  // ============================================

  /**
   * 새 목표 추가 모달 열기
   */
  const openAddGoalModal = async () => {
    showAddGoalModal.value = true;
    activeGoalTab.value = 'KPI';
    resetKpiForm();
    resetOkrForm();

    try {
      await Promise.all([
        loadParentKpiOptions(),
        loadDeptKpiOptions(),
        loadPerformanceLinkOptions(),
      ]);
    } catch (error) {
      console.error('옵션 데이터 로드 실패:', error);
    }
  };

  /**
   * 새 목표 추가 모달 닫기
   */
  const closeAddGoalModal = () => {
    showAddGoalModal.value = false;
    resetKpiForm();
    resetOkrForm();
  };

  /**
   * 새 목표 저장
   */
  const saveNewGoal = async (fetchKpiData: () => Promise<void>, selectedCycleId: string) => {
    const toast = useUiToast();

    try {
      if (activeGoalTab.value === 'KPI') {
        // ============================================================
        // [기간 + 합의 제어] (AS-IS 419/429/442 라인 규칙 이관)\n
        // - 기간 만료(kpiConfigTermYn=false)면 KPI 저장/수정/삭제 자체를 막는다.\n
        // - 합의요청(001) 또는 합의완료(003) 상태면 KPI 기본정보를 수정할 수 없고,\n
        //   요청취소(002) 또는 반려(004) 또는 합의취소(005) 상태로 돌아온 뒤에만 수정 가능.\n
        // - agreementEditYn은 백엔드가 내려주지 않아서 agreementCd로 프론트 파생한다.\n
        // ============================================================
        if (!kpiConfigTermYn.value) {
          toast.error(kpiT('toast.kpiTermExpired'));
          return false;
        }
        if (agreementYn.value && !agreementEditYn.value) {
          toast.error(kpiT('toast.kpiAgreementLocked'));
          return false;
        }

        // 유효성 검사
        if (!kpiForm.value.kpiNm?.trim()) {
          toast.error(kpiT('toast.kpiNameRequired'));
          focusFormField('kpiNm');
          return false;
        }
        if (kpiForm.value.kpiNm.length > 200) {
          toast.error(kpiT('toast.kpiNameMaxLength'));
          focusFormField('kpiNm');
          return false;
        }

        if (kpiForm.value.kpiTypeCd === '01') {
          if (!String(kpiForm.value.target).trim()) {
            toast.error(kpiT('toast.kpiQuantTargetRequired'));
            focusFormField('target');
            return false;
          }
          if (!kpiForm.value.unit?.trim()) {
            toast.error(kpiT('toast.kpiQuantUnitRequired'));
            focusFormField('unit');
            return false;
          }
        }

        if (kpiForm.value.kpiTypeCd === '02') {
          if (!String(kpiForm.value.target).trim()) {
            toast.error(kpiT('toast.kpiQualTargetRequired'));
            focusFormField('qualTarget');
            return false;
          }
        }

        if (!String(kpiForm.value.weight).trim()) {
          toast.error(kpiT('toast.kpiWeightRequired'));
          focusFormField('weight');
          return false;
        }

        // 저장 데이터 구성
        const saveData = buildKpiSaveData(kpiForm.value, {
          selectedYear: selectedYear.value,
        });

        const response = await kpiApi.saveKpi(saveData);
        const hasError = response?.msg && response?.result !== 'SUCCESS';

        if (hasError) {
          toast.error(response.msg || kpiT('toast.kpiSaveFailed'));
          return false;
        }

        toast.success(kpiT('toast.kpiSaved'));
        await fetchKpiData();
        await syncAllOkrCaches();
        memberScoreVersion.value++;
        closeAddGoalModal();
        return true;
      } else if (activeGoalTab.value === 'OKR') {
        // OKR 유효성 검사
        if (!selectedCycleId) {
          toast.error(kpiT('toast.cycleRequired'));
          return false;
        }

        if (!okrForm.value.objNm?.trim()) {
          toast.error(kpiT('toast.objectiveRequired'));
          focusOkrField('input[placeholder="새 목적을 입력하세요"]');
          return false;
        }
        if (okrForm.value.objNm.length > 200) {
          toast.error(kpiT('toast.objectiveMaxLength'));
          focusOkrField('input[placeholder="새 목적을 입력하세요"]');
          return false;
        }

        const krList = okrForm.value.krList || [];
        if (krList.length === 0 || !krList.some(kr => kr.krNm?.trim())) {
          toast.error(kpiT('toast.krMinOneRequired'));
          return false;
        }

        const targetUserId = selectedMember.value || loginUserInfo.value?.userId || '';

        if (!targetUserId) {
          toast.error(kpiT('toast.targetUserRequired'));
          return false;
        }

        // 1. objId 확보 (새 Objective면 먼저 생성)
        let objId = okrForm.value.objId;

        if (!objId && okrForm.value.objNm?.trim()) {
          const objRes = await kpiApi.saveObjective({
            year: selectedYear.value,
            cycleId: selectedCycleId,
            objNm: okrForm.value.objNm,
          });
          objId = objRes?.objId;

          if (!objId) {
            toast.error(kpiT('toast.objectiveCreateFailed'));
            return false;
          }
        }

        // 2. KR 목록 반복 저장
        for (const kr of krList) {
          if (!kr.krNm?.trim()) continue; // 빈 KR 스킵

          await kpiApi.insertOkr({
            year: selectedYear.value,
            cycleId: selectedCycleId,
            objId,
            krNm: kr.krNm,
            startDt: formatDateForApi(kr.startDt),
            endDt: formatDateForApi(kr.endDt),
            content: kr.content || '',
            evalTargetGbnCd: kr.evalTargetGbnCd || '001',
            performMeasMtd: kr.performMeasMtd || '001',
            performTarget: kr.targetVal || '',
          });
        }

        toast.success(kpiT('toast.okrSaved'));
        await fetchKpiData();
        await syncAllOkrCaches();
        memberScoreVersion.value++;
        closeAddGoalModal();
        return true;
      }
    } catch (error) {
      console.error('목표 저장 실패:', error);
      toast.error(kpiT('toast.kpiSaveError'));
      return false;
    }
  };

  // ============================================
  // KPI 수정 모달
  // ============================================

  /**
   * KPI 수정 모달 열기
   */
  const openEditKpiModal = async (kpi: KpiItem) => {
    editingKpiId.value = kpi.kpiId;
    editingKpiData.value = kpi;

    // 폼에 기존 데이터 채우기
    kpiForm.value = {
      ...createDefaultKpiForm(),
      kpiNm: kpi.kpiNm || '',
      kpiGbnId: kpi.kpiOwnerType === 'dept' ? 'department' : 'personal',
      kpiTypeCd: kpi.kpiTypeCd || '01',
      weight: String(kpi.weight || ''),
      target: kpi.targetStr?.replace(/[^\d.]/g, '') || '',
      description: kpi.description || kpi._raw?.kpiDesc || '',
      accrueMethodCd: (kpi.accrueMethodCd || '001') as '001' | '002' | '003',
      rateMethodCd: (kpi.rateMethodCd || '001') as '001' | '002',
      evalTargetGbnCd: (kpi.evalTargetGbnCd || '001') as '001' | '002',
      orgKpiId: kpi._raw?.orgKpiId || '',
      shareGbn: kpi.shareGbn || 'N',
      upKpiId: kpi._raw?.managerKpiId || kpi.upKpiId || '',
    };

    // 단위 추출
    if (kpi.targetStr) {
      const unitMatch = kpi.targetStr.match(/[^\d.,\s]+$/);
      if (unitMatch) {
        kpiForm.value.unit = unitMatch[0];
      }
    }

    // 비계량인 경우 plan을 target에 설정
    if (kpi.kpiTypeCd === '02' && kpi.plan) {
      kpiForm.value.target = kpi.plan;
    }

    showEditKpiModal.value = true;

    // 옵션 및 연결 데이터 로드
    const [, , , linkedOkrRes, linkedPlanRes] = await Promise.all([
      loadParentKpiOptions(),
      loadDeptKpiOptions(),
      loadPerformanceLinkOptions(),
      kpiApi.fetchOkrList(selectedYear.value, selectedMember.value, kpi.kpiId),
      kpiApi.fetchTaskList(selectedYear.value, selectedMember.value, kpi.kpiId),
    ]);

    // 연결된 OKR 설정
    const linkedOkrList =
      linkedOkrRes?.planList?.filter((item: PlanResponseItem) => item.checked === 'checked') || [];
    if (linkedOkrList.length > 0) {
      kpiForm.value.krIds = linkedOkrList.map((item: PlanResponseItem) => item.planId || '');
      kpiForm.value.linkType = 'okr';
    }

    // 연결된 업무 설정
    const linkedPlanList =
      linkedPlanRes?.planList?.filter((item: PlanResponseItem) => item.checked === 'checked') || [];
    if (linkedPlanList.length > 0 && kpiForm.value.linkType !== 'okr') {
      kpiForm.value.planIds = linkedPlanList.map((item: PlanResponseItem) => item.planId || '');
      kpiForm.value.linkType = 'plan';
    }
  };

  /**
   * KPI 수정 저장
   */
  const updateKpi = async (fetchKpiData: () => Promise<void>) => {
    const toast = useUiToast();

    try {
      // ============================================================
      // [기간 + 합의 제어] (AS-IS 419/429/442 라인 규칙 이관)\n
      // - 기간 만료 또는 합의 진행/완료 상태에서는 KPI 수정이 불가\n
      // ============================================================
      if (!kpiConfigTermYn.value) {
        toast.error(kpiT('toast.kpiTermExpired'));
        return false;
      }
      if (agreementYn.value && !agreementEditYn.value) {
        toast.error(
          '합의요청/합의 상태에서는 KPI를 수정할 수 없습니다. 요청취소 또는 반려/합의취소 후 다시 시도해주세요.',
        );
        return false;
      }

      // 유효성 검사
      if (!kpiForm.value.kpiNm?.trim()) {
        toast.error(kpiT('toast.kpiNameRequired'));
        focusFormField('kpiNm');
        return false;
      }

      if (kpiForm.value.kpiTypeCd === '01') {
        if (!String(kpiForm.value.target).trim()) {
          toast.error(kpiT('toast.kpiQuantTargetRequired'));
          focusFormField('target');
          return false;
        }
        if (!kpiForm.value.unit?.trim()) {
          toast.error(kpiT('toast.kpiQuantUnitRequired'));
          focusFormField('unit');
          return false;
        }
      }

      if (kpiForm.value.kpiTypeCd === '02') {
        if (!String(kpiForm.value.target).trim()) {
          toast.error(kpiT('toast.kpiQualTargetRequired'));
          focusFormField('qualTarget');
          return false;
        }
      }

      if (!String(kpiForm.value.weight).trim()) {
        toast.error(kpiT('toast.kpiWeightRequired'));
        focusFormField('weight');
        return false;
      }

      // 수정 데이터 구성
      const saveData = buildKpiSaveData(kpiForm.value, {
        selectedYear: selectedYear.value,
        kpiId: editingKpiId.value,
        originalSharedYn:
          editingKpiData.value?.sharedYn ||
          editingKpiData.value?._raw?.sharedYn ||
          (editingKpiData.value?.shareGbn === 'shared' ? 'Y' : 'N'),
        originalUpKpiId: editingKpiData.value?.upKpiId || editingKpiData.value?._raw?.upKpiId || '',
      });

      const response = await kpiApi.saveKpi(saveData);
      const hasError = response?.msg && response?.result !== 'SUCCESS';

      if (hasError) {
        toast.error(response.msg || kpiT('toast.kpiUpdateFailed'));
        return false;
      }

      toast.success(kpiT('toast.kpiUpdated'));
      await fetchKpiData();
      await syncAllOkrCaches();
      memberScoreVersion.value++;
      closeEditKpiModal();
      return true;
    } catch (error) {
      console.error('KPI 수정 실패:', error);
      toast.error(kpiT('toast.kpiUpdateError'));
      return false;
    }
  };

  /**
   * KPI 삭제
   */
  const deleteKpiFromModal = async (fetchKpiData: () => Promise<void>) => {
    if (!editingKpiId.value) return false;

    const toast = useUiToast();

    // ============================================================
    // [기간 + 합의 제어]\n
    // - 기간 만료 또는 합의 진행/완료 상태에서는 KPI 삭제도 불가\n
    // ============================================================
    if (!kpiConfigTermYn.value) {
      toast.error(kpiT('toast.kpiTermExpired'));
      return false;
    }
    if (agreementYn.value && !agreementEditYn.value) {
      toast.error(kpiT('toast.kpiAgreementLockedEdit'));
      return false;
    }

    showConfirmDialog(
      kpiT('confirm.deleteTitle'),
      kpiT('confirm.deleteKpi'),
      async () => {
        try {
          const response = await kpiApi.deleteKpi(editingKpiId.value, selectedYear.value);
          const hasError = response?.msg && response?.result !== 'SUCCESS';

          if (hasError) {
            toast.error(response.msg || kpiT('toast.kpiDeleteFailed'));
            return;
          }

          toast.success(kpiT('toast.kpiDeleted'));
          await fetchKpiData();
          await syncAllOkrCaches();
          memberScoreVersion.value++;
          closeEditKpiModal();
        } catch (error) {
          console.error('KPI 삭제 실패:', error);
          toast.error(kpiT('toast.kpiDeleteError'));
        }
      },
      undefined,
      'error',
    );
  };

  /**
   * KPI 수정 모달 닫기
   */
  const closeEditKpiModal = () => {
    showEditKpiModal.value = false;
    editingKpiId.value = '';
    editingKpiData.value = null;
    resetKpiForm();
  };

  // ============================================
  // 배포 대상 관련
  // ============================================

  const openRefUserModal = () => {
    showRefUserModal.value = true;
  };

  const closeRefUserModal = () => {
    showRefUserModal.value = false;
    refUserSearchKeyword.value = '';
  };

  const handleRefUserConfirm = (users: RefUserItem[]) => {
    // 자기 자신은 배포 대상에서 제외
    const myId = selectedMember.value || loginUserInfo.value?.userId || '';
    const hasSelf = users.some(u => u.userId === myId);
    const filtered = users.filter(u => u.userId !== myId);
    if (hasSelf) {
      const toast = useUiToast();
      toast.error(kpiT('toast.distributeSelfError'));
    }
    selectedRefUsers.value = filtered;
    kpiForm.value.refUserIds = filtered.map(u => u.userId);
    closeRefUserModal();
  };

  const removeRefUser = (userId: string) => {
    selectedRefUsers.value = selectedRefUsers.value.filter(u => u.userId !== userId);
    kpiForm.value.refUserIds = kpiForm.value.refUserIds.filter(id => id !== userId);
  };

  const clearRefUsers = () => {
    selectedRefUsers.value = [];
    kpiForm.value.refUserIds = [];
  };

  // ============================================
  // KR 추가 모달 관련
  // ============================================

  const openKrAddModal = () => {
    showKrAddModal.value = true;
    newKrForm.value = { krNm: '', weight: '' };
  };

  const closeKrAddModal = () => {
    showKrAddModal.value = false;
    newKrForm.value = { krNm: '', weight: '' };
  };

  const addKr = () => {
    if (!newKrForm.value.krNm?.trim()) return;

    addedKrList.value.push({
      krId: `temp-${Date.now()}`,
      krNm: newKrForm.value.krNm,
      weight: newKrForm.value.weight || '',
    });
    closeKrAddModal();
  };

  const removeKr = (krId: string) => {
    addedKrList.value = addedKrList.value.filter(kr => kr.krId !== krId);
  };

  // ============================================
  // OKR 배포 대상 관련
  // ============================================

  const openOkrRefUserModal = () => {
    showOkrRefUserModal.value = true;
  };

  const closeOkrRefUserModal = () => {
    showOkrRefUserModal.value = false;
  };

  const handleOkrRefUserConfirm = (users: RefUserItem[]) => {
    selectedOkrRefUsers.value = users;
    okrForm.value.refUserIds = users.map(u => u.userId);
    closeOkrRefUserModal();
  };

  const removeOkrRefUser = (userId: string) => {
    selectedOkrRefUsers.value = selectedOkrRefUsers.value.filter(u => u.userId !== userId);
    okrForm.value.refUserIds = okrForm.value.refUserIds.filter(id => id !== userId);
  };

  // ============================================
  // 참고 KPI 관련 (분리된 store 위임)
  // ============================================

  const openReferenceKpiModal = () => refKpiStore.openReferenceKpiModal();
  const closeReferenceKpiModal = () => refKpiStore.closeReferenceKpiModal();
  const searchReferenceKpi = () => refKpiStore.searchReferenceKpi();

  const selectReferenceKpi = (refKpi: RefKpiItem) => {
    const selected = refKpiStore.selectReferenceKpi(refKpi);
    kpiForm.value.kpiNm = selected.kpiNm;
    kpiForm.value.description = selected.description;
    kpiForm.value.unit = selected.unit;
  };

  return {
    // 상태
    ...useKpiFormState(),

    // KPI 연계도
    openKpiLinkModal,
    closeKpiLinkModal,

    // KR 연계도
    openKrLinkModal,
    closeKrLinkModal,

    // 폼 초기화
    resetKpiForm,
    resetOkrForm,
    resetEditingOkrData,

    // 옵션 로드
    loadParentKpiOptions,
    loadDeptKpiOptions,
    loadKpiSelectOptions,
    loadOkrOptions,
    loadPlanOptions,
    loadObjectiveOptions,
    loadPrevYearOptions,
    loadPrevKpiOptions,
    loadPerformanceLinkOptions,

    // 새 목표 모달
    openAddGoalModal,
    closeAddGoalModal,
    saveNewGoal,

    // KPI 수정
    openEditKpiModal,
    updateKpi,
    deleteKpiFromModal,
    closeEditKpiModal,

    // 배포 대상
    openRefUserModal,
    closeRefUserModal,
    handleRefUserConfirm,
    removeRefUser,
    clearRefUsers,

    // KR 추가
    openKrAddModal,
    closeKrAddModal,
    addKr,
    removeKr,

    // OKR 배포 대상
    openOkrRefUserModal,
    closeOkrRefUserModal,
    handleOkrRefUserConfirm,
    removeOkrRefUser,

    // 참고 KPI (위임)
    openReferenceKpiModal,
    closeReferenceKpiModal,
    searchReferenceKpi,
    selectReferenceKpi,
    fetchReferenceKpiList: refKpiStore.fetchReferenceKpiList,

    // 업무 옵션 로드 목록
    planBoardList,
    planCategoryList,

    // 업무 메뉴 권한
    hasAvtMenu,
  };
};
